import React, { useRef, useMemo } from 'react';
import { Box, Skeleton, Alert, Typography } from '@mui/material';
import Chart, {
  ArgumentAxis,
  ValueAxis,
  Series,
  Legend,
  Tooltip,
  CommonSeriesSettings,
  Export,
  Title,
  Font,
} from 'devextreme-react/chart';
import { useEndReasonTypeData } from '../contexts/EndReasonTypeDataContext';
import { EndReasonTypeFilterControls } from './EndReasonTypeFilterControls';
import { DashboardCard } from '../components/DashboardCard';

const END_REASON_ID_COLORS: Record<number, string> = {
  1: '#EF5350',  // Drop Out
  2: '#FF9800',  // Withdrawal
  3: '#26A69A',  // N/A
  4: '#FFA726',  // Other
  5: '#66BB6A',  // Graduated
  6: '#42A5F5',  // Persisted Next Grade
  7: '#5C6BC0',  // Persisted Same Grade
  8: '#2E7D32',  // Graduated w/ Rigorous
  9: '#81C784',  // Graduated w/o Rigorous
  10: '#757575', // Deceased
  11: '#BDBDBD', // Unknown
};

const DEFAULT_COLOR = '#9E9E9E';

interface StackedChartDataPoint {
  year: string;
  [key: string]: string | number;
}

interface TooltipArg {
  argument: string;
  seriesName: string;
  value?: number;
  point?: {
    data?: any;
  };
}

const EndReasonTypeChartContent = ({ chartRef }: { chartRef: React.RefObject<Chart> }) => {
  const { data, loading, error, filter } = useEndReasonTypeData();

  // Prepare metadata for export
  const exportMetadata = useMemo(() => {
    const yearsLabel = filter.schoolYears.length > 0
      ? filter.schoolYears.map(y => `${y}-${y + 1}`).join(', ')
      : 'All Years';
    const exportDate = new Date().toLocaleDateString();
    
    return {
      title: 'End Reason Type Distribution',
      subtitle: `Years: ${yearsLabel}`
    };
  }, [filter.schoolYears]);

  // Memoized chart data preparation
  const { chartData, endReasonTypes, typeToIdMap } = useMemo(() => {
    if (!data || data.length === 0) {
      return { chartData: [], endReasonTypes: [], typeToIdMap: {} };
    }

    const uniqueYears = [...new Set(data.map((d) => d.year))].sort();
    const uniqueTypes = [...new Set(data.map((d) => d.endReasonTypeName))].sort();

    const typeToIdMap: Record<string, number> = {};
    data.forEach(d => {
      if (!typeToIdMap[d.endReasonTypeName]) {
        typeToIdMap[d.endReasonTypeName] = d.endReasonTypeId;
      }
    });

    const mappedData: StackedChartDataPoint[] = uniqueYears.map((year) => {
      const yearData: StackedChartDataPoint = { year: `${year}-${year + 1}` };

      uniqueTypes.forEach((type) => {
        const item = data.find((d) => d.year === year && d.endReasonTypeName === type);
        yearData[type] = item?.percentageSubmitted || 0;
      });
      return yearData;
    });

    return { chartData: mappedData, endReasonTypes: uniqueTypes, typeToIdMap };
  }, [data]);

  if (loading) {
    return <Skeleton variant="rounded" height={400} />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Typography align="center" color="textSecondary">
        No end reason type data available for selected years
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <EndReasonTypeFilterControls />
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <Chart
          ref={chartRef}
          dataSource={chartData}
          height="100%"
        >
          <Title text={exportMetadata.title} subtitle={exportMetadata.subtitle}>
            <Font size={18} weight={600} />
          </Title>
          <CommonSeriesSettings
            type="stackedbar"
            barPadding={0.3}
          />
          <ArgumentAxis />
          <ValueAxis
            visualRange={{ startValue: 0, endValue: 100 }}
            tickInterval={10}
            title="Percentage (%)"
          />
          {endReasonTypes.map((type) => (
            <Series
              key={type}
              valueField={type}
              argumentField="year"
              name={type}
              color={END_REASON_ID_COLORS[typeToIdMap[type]] || DEFAULT_COLOR}
              barWidth={100}
            />
          ))}
          <Legend visible={true} horizontalAlignment="center" verticalAlignment="bottom" />
          <Tooltip
            enabled={true}
            customizeTooltip={(arg: TooltipArg) => {
              const yearString = arg.argument;
              const year = parseInt(yearString.split('-')[0]);
              const endReasonType = arg.seriesName;

              const item = data.find((d) => d.year === year && d.endReasonTypeName === endReasonType);

              if (item) {
                return {
                  text: `${endReasonType}: ${arg.value?.toFixed(1)}% (${item.studentCount}/${item.totalEnrolled} students)`,
                };
              }

              return {
                text: `${endReasonType}: ${arg.value?.toFixed(1)}%`,
              };
            }}
          />
          <Export enabled={true} />
        </Chart>
      </Box>
    </Box>
  );
};

export const EndReasonTypeChartCard = () => {
  const { filter } = useEndReasonTypeData();
  const chartRef = useRef<Chart>(null);

  const handleExport = () => {
    if (chartRef.current) {
      const yearsLabel = filter.schoolYears.length > 0
        ? filter.schoolYears.join('_')
        : 'AllYears';
      chartRef.current.instance.exportTo(`EndReasonType_${yearsLabel}`, 'png');
    }
  };

  return (
    <DashboardCard title="End Reason" onExport={handleExport}>
      <EndReasonTypeChartContent chartRef={chartRef} />
    </DashboardCard>
  );
};